import React, {memo, forwardRef} from 'react'
import {SquareShape} from './SquareShape'
import {SquareCutoutShape} from './SquareCutoutShape'
import {CircleShape} from './CircleShape'
import {CircleCutoutShape} from './CircleCutoutShape'
import {TriangleShape} from './TriangleShape'
import {PentagonShape} from './PentagonShape'
import {HexagonShape} from './HexagonShape'
import {RoundRectShape} from './RoundRectShape'
import {RoundRectCutoutShape} from './RoundRectCutoutShape'
import {StarShape} from './StarShape'
import {StarCutoutShape} from './StarCutoutShape'
import {PlusShape} from './PlusShape'
import {TagLeftShape} from './TagLeftShape'
import {TagLeftCutoutShape} from './TagLeftCutoutShape'
import {TagRightShape} from './TagRightShape'
import {TagRightCutoutShape} from './TagRightCutoutShape'
import {ChevronLeftShape} from './ChevronLeftShape'
import {ChevronLeftCutoutShape} from './ChevronLeftCutoutShape'
import {ChevronRightShape} from './ChevronRightShape'
import {ChevronRightCutoutShape} from './ChevronRightCutoutShape'
import {ArrowLeftShape} from './ArrowLeftShape'
import {ArrowLeftCutoutShape} from './ArrowLeftCutoutShape'
import {ArrowRightShape} from './ArrowRightShape'
import {ArrowRightCutoutShape} from './ArrowRightCutoutShape'
import {TemplateShape} from './TemplateShape'
import {SHAPE_TYPES} from '../../constant/shapes'

const {
    SQUARE,
    SQUARE_CUTOUT,
    CIRCLE,
    CIRCLE_CUTOUT,
    TRIANGLE,
    PENTAGON,
    HEXAGON,
    ROUND_RECT,
    ROUND_RECT_CUTOUT,
    STAR,
    STAR_CUTOUT,
    PLUS,
    TAG_LEFT,
    TAG_LEFT_CUTOUT,
    TAG_RIGHT,
    TAG_RIGHT_CUTOUT,
    CHEVRON_LEFT,
    CHEVRON_LEFT_CUTOUT,
    CHEVRON_RIGHT,
    CHEVRON_RIGHT_CUTOUT,
    ARROW_LEFT,
    ARROW_LEFT_CUTOUT,
    ARROW_RIGHT,
    ARROW_RIGHT_CUTOUT,
    TEMPLATE,
} = SHAPE_TYPES

/**
 * @typedef {import('../step2/artwork-editor/shapes/types').ShapeType} ShapeType
 */

/**
 * Create shape component
 * @param {Object} config - Shape configuration
 * @param {ShapeType} config.shapeType - Shape type
 * @param {number} [config.size] - Size of the shape
 * @param {number} [config.width] - Width of the shape
 * @param {number} [config.height] - Height of the shape
 * @param {string} [config.fill='#000'] - Fill color
 * @param {string} [config.stroke='#303238'] - Stroke color
 * @param {number} [config.strokeWidth=2] - Stroke width
 * @param {number} [config.x] - X position
 * @param {number} [config.y] - Y position
 * @param {number} [config.width] - Width of the shape
 * @param {number} [config.height] - Height of the shape
 * @param {Object} [config.template] - Template object
 * @returns {import('react-konva').KonvaNodeComponent} - Shape component
 */
export const Shape = memo(
    forwardRef((config, ref) => {
        const {shapeType, template, ...rest} = config || {}
        const props = {...rest}
        switch (shapeType) {
            /**
             * Basic shape component
             */
            case SQUARE:
                return <SquareShape ref={ref} {...props} />
            case CIRCLE:
                return <CircleShape ref={ref} {...props} />
            case TRIANGLE:
                return <TriangleShape ref={ref} {...props} />
            case PENTAGON:
                return <PentagonShape ref={ref} {...props} />
            case HEXAGON:
                return <HexagonShape ref={ref} {...props} />
            case ROUND_RECT:
                return <RoundRectShape ref={ref} {...props} />
            case STAR:
                return <StarShape ref={ref} {...props} />
            case PLUS:
                return <PlusShape ref={ref} {...props} />
            case TAG_LEFT:
                return <TagLeftShape ref={ref} {...props} />
            case TAG_RIGHT:
                return <TagRightShape ref={ref} {...props} />
            case CHEVRON_RIGHT:
                return <ChevronRightShape ref={ref} {...props} />
            case CHEVRON_LEFT:
                return <ChevronLeftShape ref={ref} {...props} />
            case ARROW_RIGHT:
                return <ArrowRightShape ref={ref} {...props} />
            case ARROW_LEFT:
                return <ArrowLeftShape ref={ref} {...props} />
            /**
             * Basic cutout shape component
             */
            case SQUARE_CUTOUT:
                return <SquareCutoutShape ref={ref} {...props} />
            case CIRCLE_CUTOUT:
                return <CircleCutoutShape ref={ref} {...props} />
            case ROUND_RECT_CUTOUT:
                return <RoundRectCutoutShape ref={ref} {...props} />
            case STAR_CUTOUT:
                return <StarCutoutShape ref={ref} {...props} />
            case TAG_LEFT_CUTOUT:
                return <TagLeftCutoutShape ref={ref} {...props} />
            case TAG_RIGHT_CUTOUT:
                return <TagRightCutoutShape ref={ref} {...props} />
            case CHEVRON_LEFT_CUTOUT:
                return <ChevronLeftCutoutShape ref={ref} {...props} />
            case CHEVRON_RIGHT_CUTOUT:
                return <ChevronRightCutoutShape ref={ref} {...props} />
            case ARROW_LEFT_CUTOUT:
                return <ArrowLeftCutoutShape ref={ref} {...props} />
            case ARROW_RIGHT_CUTOUT:
                return <ArrowRightCutoutShape ref={ref} {...props} />
            /**
             * Template shape component
             */
            case TEMPLATE: {
                props.template = template
                return <TemplateShape ref={ref} {...props} />
            }
            default:
                console.warn('Unknown shape type:', shapeType, ' | config', config)
                return null
        }
    }),
)

export default Shape
